import {createSlice, Dispatch, PayloadAction} from "@reduxjs/toolkit";
import {AccountDetailDto, AccountSubscriptionType, AccountType, NoteDto, ProjectDto, ProjectFilterInput, ProjectSearchType, ProjectService, SaleDto,} from "../../../api/psc";
import {defaultLimit} from "../../../constants/GlobalConstants";
import {addOrUpdateNoteAction, addOrUpdateSaleAction, projectUpdatedAction, projectVisitedAction,} from "../../../slices/SharedActions";
import {clearSelectedProjects, setProjectDetailIndex,} from "../../../slices/SharedSlice";
import {detectFilterChange} from "../../../utils/filter";
import {createRandomId} from "../../../utils/random";

export interface ProjectState {
    id: string;
    isLoading: boolean;
    page: number;
    projects: ProjectDto[];
    totalCount: number;
    filter: ProjectFilterInput;
    hasAnyFilter: boolean;
}

const initialState: ProjectState = {
    id: "",
    isLoading: true,
    page: 0,
    projects: new Array(defaultLimit).fill(null).map((_, i) => {
        return { hashId: i.toString() } as ProjectDto;
    }),
    totalCount: 0,
    filter: {
        term: "",
        type: ProjectSearchType.NEW,
        onlyWithConstructor: true,
        cityIds: [],
        regionIds: [],
        subRegionIds: [],
        phaseIds: [],
        folderIds: [],
        usageTypesIds: [],
        structureTypeIds: [],
        lastPhaseUpdateDateType: null,
        subscriptionTypes: [],
    },
    hasAnyFilter: false,
};

const projectSlice = createSlice({
    name: "projects",
    initialState,
    reducers: {
        setInitialProjectAccess(
            state: ProjectState,
            action: PayloadAction<AccountDetailDto | null>
        ) {
            const filter = { ...state.filter };

            if (action.payload && action.payload.accessList.length > 0) {
                filter.targetAccessId = action.payload.accessList[0].id;
                if (action.payload.type === AccountType.TEST) {
                    filter.type = ProjectSearchType.ALL;
                }
            } else {
                filter.type = ProjectSearchType.ALL;
            }

            return {
                ...initialState,
                filter: filter,
            };
        },
        setProjectsFilter(
            state: ProjectState,
            action: PayloadAction<ProjectFilterInput>
        ) {
            const isTargetAccessChanged =
                state.filter.targetAccessId !== action.payload.targetAccessId;
            const isTypeChanged = state.filter.type !== action.payload.type;

            const id =
                isTypeChanged || isTargetAccessChanged
                    ? createRandomId(5)
                    : state.id;
            const page =
                isTypeChanged || isTargetAccessChanged ? 0 : state.page;
            const isLoading =
                state.isLoading || isTypeChanged || isTargetAccessChanged;

            return {
                ...state,
                filter: action.payload,
                hasAnyFilter: detectFilterChange(
                    initialState.filter,
                    action.payload
                ),
                id: id,
                page: page,
                isLoading: isLoading,
            };
        },
        setProjectsPage(state: ProjectState, action: PayloadAction<number>) {
            return {
                ...state,
                page: action.payload,
                isLoading: true,
                id: createRandomId(5),
            };
        },
        setProjects(
            state: ProjectState,
            action: PayloadAction<{
                projects: ProjectDto[];
                totalCount: number;
            }>
        ) {
            return {
                ...state,
                isLoading: false,
                totalCount: action.payload.totalCount,
                projects: action.payload.projects,
            };
        },
        reloadProject(state: ProjectState, _action: PayloadAction) {
            return {
                ...state,
                isLoading: true,
                id: createRandomId(5),
            };
        },
        resetProjectFilter(state: ProjectState, _action: PayloadAction) {
            return {
                ...state,
                hasAnyFilter: false,
                filter: {
                    ...initialState.filter,
                    type: state.filter.type,
                    targetAccessId: state.filter.targetAccessId,
                },
            };
        },
        setViewInSubscriptionMode(
            state: ProjectState,
            _action: PayloadAction<AccountSubscriptionType[]>
        ) {
            return {
                ...state,
                hasAnyFilter: true,
                isLoading: true,
                filter: {
                    ...initialState.filter,
                    type: ProjectSearchType.ALL,
                    subscriptionTypes: _action.payload,
                },
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            projectUpdatedAction,
            (s: ProjectState, a: PayloadAction<ProjectDto>) => {
                const project = s.projects.find(
                    (p) => p.hashId === a.payload.hashId
                );
                const updatedProjectList = [...s.projects];

                if (project) {
                    const indexOfProject = updatedProjectList.indexOf(project);
                    updatedProjectList[indexOfProject] = a.payload;
                }

                return { ...s, projects: updatedProjectList };
            }
        );
        builder.addCase(
            projectVisitedAction,
            (s: ProjectState, a: PayloadAction<string>) => {
                const project = s.projects.find((p) => p.hashId === a.payload);
                const updatedProjectList = [...s.projects];

                if (project) {
                    const index = updatedProjectList.indexOf(project);
                    updatedProjectList[index] = { ...project, isVisited: true };
                }

                return {
                    ...s,
                    projects: updatedProjectList,
                };
            }
        );
        builder.addCase(
            addOrUpdateSaleAction,
            (s: ProjectState, a: PayloadAction<SaleDto>) => {
                const updatedProjects = [...s.projects];
                const project = updatedProjects.find(
                    (p) => p.hashId === a.payload.projectHashId
                );

                if (project) {
                    const updatedProject = { ...project };
                    updatedProject.hasSale = true;

                    const index = updatedProjects.indexOf(project);

                    updatedProjects[index] = updatedProject;
                }

                return { ...s, projects: updatedProjects };
            }
        );
        builder.addCase(
            addOrUpdateNoteAction,
            (s: ProjectState, a: PayloadAction<NoteDto>) => {
                const updatedProjects = [...s.projects];
                const project = updatedProjects.find(
                    (p) => p.hashId === a.payload.projectHashId
                );

                if (project) {
                    const updatedProject = { ...project };
                    updatedProject.hasNote = true;

                    const index = updatedProjects.indexOf(project);

                    updatedProjects[index] = updatedProject;
                }

                return { ...s, projects: updatedProjects };
            }
        );
    },
});

export async function FetchProjects(state: ProjectState, dispatch: Dispatch) {
    const projects = await ProjectService.filterProjects(
        "1",
        defaultLimit,
        state.page * defaultLimit,
        state.filter
    );
    dispatch(
        setProjects({
            projects: projects.results,
            totalCount: projects.count,
        })
    );
    dispatch(clearSelectedProjects());
    dispatch(setProjectDetailIndex(0));
}

export const {
    setInitialProjectAccess,
    setProjectsFilter,
    setProjects,
    setProjectsPage,
    reloadProject,
    resetProjectFilter,
    setViewInSubscriptionMode,
} = projectSlice.actions;
export default projectSlice.reducer;
